import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import api, { imgSrc } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Lightbox from "@/components/Lightbox";
import { LuxButton, Eyebrow } from "@/components/lux";

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/portfolio/projects/${slug}`).then((r) => setProject(r.data)).catch(() => setProject(false));
  }, [slug]);

  if (project === false) {
    return (
      <div className="bg-[#090A0F] min-h-screen">
        <Header />
        <div className="pt-40 text-center text-white">
          <p className="font-display text-2xl">Project not found</p>
          <LuxButton className="mt-6" onClick={() => navigate("/")}>Back Home</LuxButton>
        </div>
      </div>
    );
  }
  if (!project) return <div className="bg-[#090A0F] min-h-screen" />;

  const gallery = project.gallery || [];

  return (
    <div className="bg-[#090A0F] min-h-screen">
      <Header />
      <main className="pt-28 sm:pt-36">
        <div className="mx-auto max-w-[1300px] px-5 sm:px-8">
          <button onClick={() => navigate("/#portfolio")} className="flex items-center gap-2 text-[#94A3B8] hover:text-[#02AACF] text-sm mb-8" data-testid="project-back">
            <ArrowLeft size={16} /> Back to portfolio
          </button>
          <Eyebrow>{project.category}</Eyebrow>
          <h1 className="font-display font-bold text-white text-4xl sm:text-6xl mt-3">{project.name}</h1>
          <div className="grid sm:grid-cols-4 gap-6 mt-8 border-y border-white/10 py-6">
            {[["Client", project.client], ["Location", project.location], ["Date", project.date], ["Category", project.category]].map(([l, v]) => v ? (
              <div key={l}><p className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-[#64748B]">{l}</p><p className="text-white mt-1">{v}</p></div>
            ) : null)}
          </div>
          {project.description && <p className="text-[#94A3B8] text-lg leading-relaxed max-w-3xl mt-8">{project.description}</p>}
        </div>

        <div className="mx-auto max-w-[1500px] px-5 sm:px-8 mt-14 columns-1 sm:columns-2 lg:columns-3 gap-5">
          {gallery.map((g, i) => (
            <motion.button key={g.id} onClick={() => setLightbox(i)} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 6) * 0.05 }}
              className="w-full mb-5 rounded-xl overflow-hidden border border-white/5 break-inside-avoid block group" data-testid={`project-image-${i}`}>
              <img src={imgSrc(g)} alt={g.alt_text} className="w-full group-hover:scale-105 transition-transform duration-700" loading="lazy" />
            </motion.button>
          ))}
          {gallery.length === 0 && <p className="text-[#64748B] font-mono2 text-sm">No images added to this project yet.</p>}
        </div>

        <div className="text-center mt-16 pb-10">
          <LuxButton size="lg" onClick={() => navigate("/book")} data-testid="project-book">BOOK A SIMILAR SHOOT</LuxButton>
        </div>
      </main>
      <Lightbox images={gallery} index={lightbox} onClose={() => setLightbox(null)} onIndex={setLightbox} />
      <Footer />
    </div>
  );
}
